import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import BarrioBucksBalanceAnimation from './BarrioBucksBalanceAnimation';

const API_URL = 'https://barriobucks.com/api';

const stripePromise = loadStripe('pk_live_51NRY6oC5fulBWHCX11K3NHHrGCC1RRsNSnySq2ubey47C0m5UqXeAUJ0fqdTP768OmWdj04Leii5v2XiidewiIY700rPzlpSMj', {
  apiVersion: '2023-08-16',
  stripeAccount: 'acct_1NRY6oC5fulBWHCX',
});

const formatPhoneNumber = (input) => {
  const digits = input.replace(/\D/g, '');
  return digits.length === 10 ? digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : digits;
};

const PaymentForm = ({ customer, onPaymentSuccess }) => {
  console.log('PaymentForm received customer:', customer);
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState('');
  const amountRef = useRef('');
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [initialBalance, setInitialBalance] = useState(customer.barrio_bucks);
  const [finalBalance, setFinalBalance] = useState(customer.barrio_bucks);
  const [showAnimation, setShowAnimation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const processedPayments = useRef(new Set());

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      amountRef.current = value;
      // Don't update paymentRequest here to avoid mutable property error
      // The amount will be handled in the payment method event
    }
  };

  const createPaymentIntent = async (amount) => {
    if (isNaN(amount) || amount <= 0) {
      return { error: { message: 'Invalid amount' } };
    }
    const payload = { amount: Math.round(amount * 100) };
    try {
      console.log('Sending create payment intent request with payload:', payload);
      const response = await axios.post(`${API_URL}/create-payment-intent`, payload);
      console.log('Received response from create payment intent:', response.data);
      return { 
        clientSecret: response.data.clientSecret,
        paymentIntentId: response.data.paymentIntentId
      };
    } catch (err) {
      console.error('Error creating payment intent:', err);
      return { error: { message: err.response ? err.response.data.error : err.message } };
    }
  };

  const handlePaymentSuccess = useCallback(async (amount, paymentIntentId) => {
    try {
      console.log('Handling payment success', { amount, paymentIntentId, customer });
      const amountInCents = Math.round(parseFloat(amount) * 100);
      const response = await axios.post(`${API_URL}/payment-success`, {
        phone: customer.phone,
        amount: amountInCents,
        customerId: customer.id,
        paymentIntentId: paymentIntentId
      });
      console.log('Payment success response:', response.data);
      setPaymentSuccess(true);
      const newBalance = response.data.customer.barrio_bucks;
      setInitialBalance(customer.barrio_bucks);
      setFinalBalance(newBalance);
      setShowAnimation(true);
      onPaymentSuccess(newBalance, amountInCents / 100);
      setError('');
    } catch (err) {
      console.error('Error sending payment success to server:', err);
      setError('Payment processed, but there was an error updating your account. Please contact support.');
    }
  }, [customer, onPaymentSuccess]);

  useEffect(() => {
    if (stripe) {
      // Only create PaymentRequest if we're in a secure context and on a supported browser
      if (window.isSecureContext && (window.PaymentRequest || window.ApplePaySession)) {
        const pr = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Barrio Bucks Payment',
            amount: 100, // Start with $1.00
          },
          requestPayerName: true,
          requestPayerEmail: true,
          requestPayerPhone: false,
          requestShipping: false,
          displayItems: [],
        });

        pr.canMakePayment().then(result => {
          if (result) {
            console.log('Digital wallet payment available');
            setPaymentRequest(pr);
          }
        }).catch(error => {
          // Silently handle PaymentRequest errors to avoid console spam
          console.debug('PaymentRequest not available:', error.message);
        });
        
        pr.on('paymentmethod', async (ev) => {
          if (isProcessing) {
            ev.complete('fail');
            setError('Payment is already being processed');
            return;
          }
          
          const currentAmount = parseFloat(amountRef.current);
          if (isNaN(currentAmount) || currentAmount <= 0) {
            ev.complete('fail');
            setError('Invalid amount');
            return;
          }
          
          setIsProcessing(true);
          setError('');
          
          try {
            const { error: backendError, clientSecret, paymentIntentId } = await createPaymentIntent(currentAmount);
          
            if (backendError) {
              ev.complete('fail');
              setError(backendError.message);
              return;
            }
            
            // Check if this payment has already been processed
            if (processedPayments.current.has(paymentIntentId)) {
              ev.complete('fail');
              setError('Payment already processed');
              return;
            }
            
            processedPayments.current.add(paymentIntentId);
        
            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
              clientSecret,
              { payment_method: ev.paymentMethod.id },
              { handleActions: false }
            );
        
            if (stripeError) {
              ev.complete('fail');
              setError(stripeError.message);
              processedPayments.current.delete(paymentIntentId);
            } else {
              ev.complete('success');
              if (paymentIntent.status === 'succeeded') {
                await handlePaymentSuccess(currentAmount, paymentIntent.id);
              }
            }
          } catch (error) {
            ev.complete('fail');
            setError('Payment processing failed');
            console.error('Apple Pay error:', error);
          } finally {
            setIsProcessing(false);
          }
        });

        return () => {
          pr.off('paymentmethod');
        };
      }
    }
  }, [stripe, handlePaymentSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isProcessing) {
      setError('Payment is already being processed');
      return;
    }
    
    const amountInCents = Math.round(parseFloat(amountRef.current) * 100);
  
    if (isNaN(amountInCents) || amountInCents <= 0) {
      setError('Invalid amount. Please enter a valid number.');
      return;
    }
    if (!stripe || !elements || !amount) {
      return;
    }
    
    setIsProcessing(true);
    setError('');
  
    try {
      console.log('Creating payment intent for amount:', amountInCents);
      const result = await createPaymentIntent(parseFloat(amount));
  
      if (result.error) {
        setError(result.error.message);
        return;
      }
      
      // Check if this payment has already been processed
      if (processedPayments.current.has(result.paymentIntentId)) {
        setError('Payment already processed');
        return;
      }
      
      processedPayments.current.add(result.paymentIntentId);
  
      console.log('Payment intent created, confirming payment:', result);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(result.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });
  
      console.log('Payment confirmation result:', { stripeError, paymentIntent });
  
      if (stripeError) {
        setError(stripeError.message);
        processedPayments.current.delete(result.paymentIntentId);
      } else if (paymentIntent.status === 'succeeded') {
        await handlePaymentSuccess(amount, result.paymentIntentId);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div>
      {!paymentSuccess ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount ($)</label>
            <input
              id="amount"
              type="number"
              value={amount}
              onChange={handleAmountChange}
              placeholder="Enter amount"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
            />
          </div>
          {paymentRequest && (
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              className="w-full"
            />
          )}
          <div>
            <label htmlFor="card-element" className="block text-sm font-medium text-gray-700">Credit or debit card</label>
            <div className="w-full overflow-hidden">
              <CardElement
                id="card-element"
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={!stripe || !amount || isProcessing}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Processing...' : `Pay $${amount || '0'}`}
          </button>
        </form>
      ) : (
        <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          <h2 className="text-2xl font-semibold mb-2">Thanks, {customer.name}!</h2>
          <p className="font-semibold mb-2">Payment successful! Thank you for your purchase.</p>
          <p className="text-lg font-semibold">Your new Barrio Bucks Balance:</p>
          {showAnimation && (
            <BarrioBucksBalanceAnimation 
              initialBalance={initialBalance}
              finalBalance={finalBalance}
            />
          )}
          <p className="mt-2">You just added ${parseFloat(amount).toFixed(2)}!</p>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

const Home = ({ onLogin }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' | 'email'
  const [loginEmail, setLoginEmail] = useState('');
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [customer, setCustomer] = useState(null);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);

  useEffect(() => {
    const storedCustomer = localStorage.getItem('customer') || sessionStorage.getItem('customer');
    if (storedCustomer) {
      const parsedCustomer = JSON.parse(storedCustomer);
      console.log('Parsed customer data:', parsedCustomer);
      setCustomer(parsedCustomer);
      onLogin(parsedCustomer);
    }
  }, [onLogin]);

  const handleLogin = useCallback((customerData) => {
    setCustomer(customerData);
    onLogin(customerData);
    if (keepLoggedIn) {
      localStorage.setItem('customer', JSON.stringify(customerData));
    } else {
      sessionStorage.setItem('customer', JSON.stringify(customerData));
    }
  }, [keepLoggedIn, onLogin]);

  const handlePhoneChange = (e) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  };

  const handleSendCode = async () => {
    try {
      if (loginMethod === 'email') {
        await axios.post(`${API_URL}/auth/initiate-email`, { email: loginEmail.trim() });
      } else {
        await axios.post(`${API_URL}/auth/initiate`, { phone: phoneNumber });
      }
      setIsCodeSent(true);
      setError('');
    } catch (err) {
      setError('Failed to send verification code. Please try again.');
    }
  };


  // const handleVerifyCode = async (code = verificationCode) => {
  //   try {
  //     const response = await axios.post(`${API_URL}/auth/verify`, { phone: phoneNumber, code: code });
  //     if (response.data.newCustomer) {
  //       setIsNewCustomer(true);
  //       setError('');
  //     } else {
  //       const customerData = {
  //         ...response.data.customer,
  //         phone: phoneNumber,
  //         isAdmin: response.data.customer.isAdmin
  //       };
  //       handleLogin(customerData);
  //       setError('');
  //     }
  //   } catch (err) {
  //     setError('Invalid verification code. Please try again.');
  //   }
  // };

  const handleVerifyCode = async (code = verificationCode) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/verify`,
        loginMethod === 'email'
          ? { email: loginEmail.trim(), code: code }
          : { phone: phoneNumber, code: code }
      );
      console.log('Verification response:', response.data);
      
      if (response.data.customer) {
        // Existing customer found
        const customerData = {
          ...response.data.customer,
          phone: phoneNumber, // Ensure phone is always set from the login form
        };
        console.log('Customer data with phone and admin level:', customerData);
        handleLogin(customerData);
        setError('');
      } else if (response.data.newCustomer) {
        // New customer needs registration
        setIsNewCustomer(true);
        setError('');
      } else {
        // Unexpected response
        setError('Unexpected response from server. Please try again.');
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    }
  };

  
  const handleVerificationCodeChange = (e, index) => {
    const value = e.target.value;
    if (value.length <= 1) {
      const newCode = verificationCode.slice(0, index) + value + verificationCode.slice(index + 1);
      setVerificationCode(newCode);
      
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        if (nextInput) {
          nextInput.focus();
        }
      }
      if (newCode.length === 6) {
        handleVerifyCode(newCode);
      }
    }
  };

  // const handleRegister = async () => {
  //   try {
  //     const response = await axios.post(`${API_URL}/auth/register`, {
  //       phone: phoneNumber,
  //       firstName: firstName,
  //       lastName: lastName
  //     });
  //     const newCustomer = {
  //       ...response.data.customer,
  //       phone: phoneNumber
  //     };
  //     handleLogin(newCustomer);
  //     setError('');
  //   } catch (err) {
  //     setError('Failed to register. Please try again.');
  //   }
  // };
  
  const handleRegister = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        phone: phoneNumber,
        firstName: firstName,
        lastName: lastName
      });
      const newCustomer = {
        ...response.data.customer,
        phone: phoneNumber
      };
      handleLogin(newCustomer);
      setError('');
    } catch (err) {
      setError('Failed to register. Please try again.');
    }
  };
  if (customer) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
        <div className="p-8">
          <h2 className="text-2xl font-semibold text-gray-800">Welcome, {customer.name || 'Valued Customer'}!</h2>
          <p className="mt-2 text-gray-600">Your Barrio Bucks balance: ${(customer.barrio_bucks || 0).toFixed(2)}</p>
          <div className="mt-6">
            <Elements stripe={stripePromise}>
              <PaymentForm 
                customer={customer}
                onPaymentSuccess={(newBalance, amount) => {
                  setCustomer(prevCustomer => ({
                    ...prevCustomer,
                    barrio_bucks: newBalance,
                    lastPurchaseAmount: amount
                  }));
                }} 
              />
            </Elements>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
      <div className="p-8">
        <h2 className="text-2xl font-semibold text-gray-800">Welcome to Barrio Bucks</h2>
   
        {!isCodeSent ? (
          <div className="mt-6">
            {loginMethod === 'phone' ? (
              <>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Enter your phone number to log in or sign up:</label>
                <input
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="(###) ###-####"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
                />
                <button
                  type="button"
                  onClick={() => { setLoginMethod('email'); setError(''); }}
                  className="mt-2 text-xs text-indigo-600 underline"
                >
                  Use my email instead
                </button>
              </>
            ) : (
              <>
                <label htmlFor="loginEmail" className="block text-sm font-medium text-gray-700">Enter the email on your profile:</label>
                <input
                  id="loginEmail"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
                />
                <p className="mt-1 text-xs text-gray-500">Your orders live on your phone number — email works once it's saved on your profile.</p>
                <button
                  type="button"
                  onClick={() => { setLoginMethod('phone'); setError(''); }}
                  className="mt-2 text-xs text-indigo-600 underline"
                >
                  Use my phone number instead
                </button>
              </>
            )}
            <div className="mt-2">
              <input
                id="keepLoggedIn"
                type="checkbox"
                checked={keepLoggedIn}
                onChange={(e) => setKeepLoggedIn(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="keepLoggedIn" className="text-sm text-gray-700">Keep me logged in on this device</label>
            </div>
            <button 
              onClick={handleSendCode} 
              disabled={loginMethod === 'email' ? !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(loginEmail.trim()) : phoneNumber.length !== 14}
              className="mt-3 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Send Verification Code
            </button>
          </div>
        ) : isNewCustomer ? (
          <div className="mt-6 space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-black"
              />
            </div>
            <button 
              onClick={handleRegister}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Complete Registration
            </button>
          </div>
        ) : (
          <div className="mt-6">
            <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">Enter the 6-digit verification code sent to your phone:</label>
            <div className="flex mt-1 space-x-2">
              {[...Array(6)].map((_, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={verificationCode[index] || ''}
                  onChange={(e) => handleVerificationCodeChange(e, index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !e.target.value && index > 0) {
                      const prevInput = e.target.previousElementSibling;
                      prevInput.focus();
                      prevInput.setSelectionRange(1, 1);
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
                    const newCode = pastedData.padEnd(6, ' ');
                    setVerificationCode(newCode);
                    if (pastedData.length === 6) {
                      handleVerifyCode(pastedData);
                    } else if (pastedData.length > 0) {
                      document.getElementById(`code-${pastedData.length}`).focus();
                    }
                  }}
                  id={`code-${index}`}
                  className="w-12 h-12 text-center text-xl border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-black"
                />
              ))}
            </div>
            <button 
              onClick={() => handleVerifyCode(verificationCode)}
              className="mt-3 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Verify Code
            </button>
          </div>
        )}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
};

export default Home;
